// AccessCodeCarousel.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  MobileStepper,
} from "@mui/material";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "upcoming", label: "Upcoming" },
  { key: "expired", label: "Expired" },
];

const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

const getStatus = (code, now) => {
  const startsAt = code?.starts_at ? new Date(code.starts_at) : null;
  const endsAt = code?.ends_at ? new Date(code.ends_at) : null;

  const hasStart = startsAt && isValidDate(startsAt);
  const hasEnd = endsAt && isValidDate(endsAt);

  if (hasEnd && endsAt < now) return "expired";
  if (hasStart && startsAt > now) return "upcoming";
  if ((hasStart && startsAt <= now) && (!hasEnd || endsAt >= now)) return "active";

  // If dates missing/invalid, keep it visible under "All"
  return "all";
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (!isValidDate(d)) return null;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const AccessCodeCarousel = ({
  accessCodes = [],
  isLoading = false,
  onDelete, // (accessCodeId, deviceId) => void
  pageSize = 4,
}) => {
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(0);

  const processed = useMemo(() => {
    const withStatus = (accessCodes || []).map((c) => ({
      ...c,
      _status: getStatus(c, new Date()),
    }));

    // Sort: newest start date first (fallback: keep stable)
    withStatus.sort((a, b) => {
      const ad = a?.starts_at ? new Date(a.starts_at) : null;
      const bd = b?.starts_at ? new Date(b.starts_at) : null;
      const at = ad && isValidDate(ad) ? ad.getTime() : 0;
      const bt = bd && isValidDate(bd) ? bd.getTime() : 0;
      return bt - at;
    });

    return withStatus;
  }, [accessCodes]);

  const filtered = useMemo(() => {
    if (tab === "all") return processed;
    return processed.filter((c) => c._status === tab);
  }, [processed, tab]);

  const pages = useMemo(() => chunk(filtered, pageSize), [filtered, pageSize]);
  const currentPageItems = pages[page] || [];
  const maxPages = pages.length;

  // Reset page when tab/data changes to avoid empty page index
  useEffect(() => {
    setPage(0);
  }, [tab, accessCodes.length]);

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => Math.min(maxPages - 1, p + 1));

  if (!accessCodes?.length) {
    return <Typography>No access codes found for this lock.</Typography>;
  }

  return (
    <Box>
      {/* Status "organizer" */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1 }}
      >
        {STATUS_TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>

      {/* "Carousel" pages */}
      <List>
        {currentPageItems.map((code) => (
          <ListItem
            key={code.access_code_id}
            secondaryAction={
              <Button
                variant="outlined"
                color="error"
                onClick={() => onDelete?.(code.access_code_id, code.device_id)}
                disabled={isLoading}
              >
                Delete
              </Button>
            }
          >
            <ListItemText
              primary={`${code.name} (${code.code || "Generated"})`}
              secondary={
                <>
                  <Typography>ID: {code.access_code_id}</Typography>
                  <Typography>Device ID: {code.device_id}</Typography>

                  {formatDate(code.starts_at) && (
                    <Typography>Start: {formatDate(code.starts_at)}</Typography>
                  )}

                  {formatDate(code.ends_at) && (
                    <Typography>Expires: {formatDate(code.ends_at)}</Typography>
                  )}
                </>
              }
            />
          </ListItem>
        ))}
      </List>

      {/* Page navigation */}
      {maxPages > 1 && (
        <MobileStepper
          variant="dots"
          steps={maxPages}
          position="static"
          activeStep={page}
          nextButton={
            <Button size="small" onClick={handleNext} disabled={page >= maxPages - 1}>
              Next
            </Button>
          }
          backButton={
            <Button size="small" onClick={handlePrev} disabled={page === 0}>
              Back
            </Button>
          }
        />
      )}

      {maxPages === 0 && (
        <Typography sx={{ mt: 1 }}>
          No access codes in this category.
        </Typography>
      )}
    </Box>
  );
};

export default AccessCodeCarousel;
